import * as React from 'react'
import * as classNames from 'classnames'
import { motion } from 'framer-motion'
import { Control, UseFormRegister, FieldErrors } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'

import { InputNumber } from '~/atoms/form/InputNumber'
import { InputDate } from '~/atoms/form/InputDate'
import { InputSelect } from '~/atoms/form/InputSelect'
import { InputRadio } from '~/atoms/form/InputRadio'
import { InputTextArea } from '~/atoms/form/InputTextArea'
import { makeZeroPaddingNumberArray } from '@/utils/arrayUtil'

import * as Style from './style.css'

export interface IProps {
  index: number
  minDate?: Date
  validation?: boolean
  animation?: boolean
  supportEvent: ISupportEventEating
  control: Control<ICompleteReportFormStateStep2>
  errors: FieldErrors<ICompleteReportFormStateStep2>
  register: UseFormRegister<ICompleteReportFormStateStep2>
  deleteHandler: (index: number) => void
}

const INIT_ANIMATION_SETTING = {
  initial: { background: '#FDEEF5' },
  animate: { background: '#FFF' },
  transition: { duration: 3 },
}

const NONE_ANIMATION_SETTING = {
  initial: { background: '#FFF' },
  animate: { background: '#FFF' },
}

const HOUR_RANGE = makeZeroPaddingNumberArray(0, 23, 2)
const MINUTE_RANGE = makeZeroPaddingNumberArray(0, 59, 2)

export const SupportEventEating = ({
  index,
  minDate,
  validation = true,
  animation = false,
  supportEvent,
  control,
  errors,
  register,
  deleteHandler,
}: IProps): JSX.Element => {
  const hasError = !!errors?.supportEvents?.[index]

  return (
    <motion.div
      {...(animation ? INIT_ANIMATION_SETTING : NONE_ANIMATION_SETTING)}
    >
      <div
        className={classNames({
          [Style.SupportEvent]: !hasError,
          [Style.SupportEventError]: hasError,
        })}
      >
        <button
          type="button"
          onClick={() => deleteHandler(index)}
          className={Style.SupportEventDelete}
        >
          <FontAwesomeIcon icon={faXmark} />
          削除
        </button>
        <div>
          <p className={Style.SupportEventName}>飲食</p>
          <ul className={Style.SupportEventDescription}>
            <li>・食事内容と量は必ずメモ欄へ記載してください。</li>
          </ul>
        </div>
        <div className={Style.SupportEventRow}>
          <p className={Style.SupportEventRowName}>
            日時
            {validation && (
              <span className={Style.SupportEventRowNameRequired}>必須</span>
            )}
          </p>
          <div className={Style.SupportEventInput}>
            <div className={Style.SupportEventDate}>
              <div className={Style.SupportEventDateDay}>
                <InputDate
                  defaultDate={supportEvent.startDate}
                  minDate={minDate}
                  control={control}
                  {...register(`supportEvents.${index}.startDate`)}
                />
              </div>
              <InputSelect
                {...register(`supportEvents.${index}.startHour`, {
                  required: validation && '入力必須項目です',
                })}
                value={supportEvent.startHour}
                isError={!!errors?.supportEvents?.[index]?.startHour}
              >
                {HOUR_RANGE.map((hour) => (
                  <option
                    key={`supportEvents.${index}.startHour.${hour}`}
                    value={hour}
                  >
                    {hour}
                  </option>
                ))}
              </InputSelect>
              <span className={Style.SupportEventDateSeparator}>：</span>
              <InputSelect
                {...register(`supportEvents.${index}.startMinute`, {
                  required: validation && '入力必須項目です',
                })}
                value={supportEvent.startMinute}
                isError={!!errors?.supportEvents?.[index]?.startMinute}
              >
                {MINUTE_RANGE.map((minute) => (
                  <option
                    key={`supportEvents.${index}.startMinute.${minute}`}
                    value={minute}
                  >
                    {minute}
                  </option>
                ))}
              </InputSelect>
            </div>
          </div>
        </div>
        <div className={Style.SupportEventRow}>
          <p className={Style.SupportEventRowName}>
            内容
            {validation && (
              <span className={Style.SupportEventRowNameRequired}>必須</span>
            )}
          </p>
          <div>
            <ul className={Style.SupportEventKind}>
              <li>
                <InputRadio
                  label="ミルク(調乳あり)"
                  value="milk_prepared"
                  {...register(`supportEvents.${index}.kind`, {
                    required: validation && '入力必須項目です',
                  })}
                />
              </li>
              <li>
                <InputRadio
                  label="ミルク(調乳なし)"
                  value="milk_unprepared"
                  {...register(`supportEvents.${index}.kind`, {
                    required: validation && '入力必須項目です',
                  })}
                />
              </li>
              <li>
                <InputRadio
                  label="離乳食"
                  value="baby_food"
                  {...register(`supportEvents.${index}.kind`, {
                    required: validation && '入力必須項目です',
                  })}
                />
              </li>
              <li>
                <InputRadio
                  label="おやつ"
                  value="snack"
                  {...register(`supportEvents.${index}.kind`, {
                    required: validation && '入力必須項目です',
                  })}
                />
              </li>
              <li>
                <InputRadio
                  label="ごはん"
                  value="meal"
                  {...register(`supportEvents.${index}.kind`, {
                    required: validation && '入力必須項目です',
                  })}
                />
              </li>
              <li>
                <span>
                  <InputRadio
                    label="のみもの"
                    value="drink"
                    {...register(`supportEvents.${index}.kind`, {
                      required: validation && '入力必須項目です',
                    })}
                  />
                </span>
              </li>
            </ul>
            <div className={Style.SupportEventInputError}>
              <ErrorMessage
                errors={errors}
                name={`supportEvents.${index}.kind`}
                render={({ message }) => <p>{message}</p>}
              />
            </div>
          </div>
        </div>
        <div className={Style.SupportEventRow}>
          <p className={Style.SupportEventKindDescription}>
            ※ミルク(調乳あり) または
            ミルク(調乳なし)の場合は、必ず量を入力してください。
          </p>
          <div className={Style.SupportEventValue}>
            <InputNumber
              {...register(`supportEvents.${index}.value`, {
                required:
                  validation &&
                  (supportEvent.kind === 'milk_prepared' ||
                    supportEvent.kind === 'milk_unprepared') &&
                  '入力必須項目です',
              })}
            />
            <span className={Style.SupportEventValueUnit}>ml</span>
          </div>
        </div>
        <div className={Style.SupportEventInputError}>
          <ErrorMessage
            errors={errors}
            name={`supportEvents.${index}.value`}
            render={({ message }) => <p>{message}</p>}
          />
        </div>
        <div className={Style.SupportEventRow}>
          <p className={Style.SupportEventRowName}>
            メモ
            {validation && (
              <span className={Style.SupportEventRowNameRequired}>必須</span>
            )}
          </p>
          <div>
            <div className={Style.SupportEventMemo}>
              <InputTextArea
                rows={4}
                placeholder={
                  '例1）焼き魚、肉じゃが、豆腐とわかめの味噌汁、白米。肉じゃがの人参半分残し、他は完食。\n例2）いちごゼリー2個完食'
                }
                {...register(`supportEvents.${index}.note`, {
                  required: validation && '入力必須項目です',
                })}
              />
            </div>
            <div className={Style.SupportEventInputError}>
              <ErrorMessage
                errors={errors}
                name={`supportEvents.${index}.note`}
                render={({ message }) => <p>{message}</p>}
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
